import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { eur } from "@/lib/apiClient";
import { Users, Shield, Info, Percent, User, Baby } from "lucide-react";

const FRAZ_LABEL = { annuale: "Annuale", semestrale: "Semestrale", trimestrale: "Trimestrale", bimestrale: "Bimestrale", mensile: "Mensile" };

function Num({ value, min, max, step, onChange, suffix, testid }) {
  return (
    <div className="flex items-center gap-2">
      <Input type="number" value={value} min={min} max={max} step={step} data-testid={testid}
        onChange={(e) => onChange(Number(e.target.value))} className="h-9 w-32" />
      {suffix && <span className="text-xs text-muted-foreground">{suffix}</span>}
    </div>
  );
}

function PersonPanel({ cfg, p, idx, onChange }) {
  const set = (k, v) => onChange(idx, { ...p, [k]: v });
  const G = cfg.garanzie;
  return (
    <Card className="rounded-xl border-border/60 p-4" data-testid={`fs-person-${idx}`}>
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-forest">
        {p.ruolo === "figlio" ? <Baby className="h-4 w-4" /> : <User className="h-4 w-4" />}
        {p.ruolo === "figlio" ? `Figlio ${idx + 1}` : `Adulto ${idx + 1}`}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs">Invalidità Permanente (capitale)</Label>
          <Num value={p.invalidita_permanente} min={G.invalidita_permanente.min} max={G.invalidita_permanente.max}
            step={G.invalidita_permanente.step} suffix="€" onChange={(v) => set("invalidita_permanente", v)} testid={`fs-ip-${idx}`} />
        </div>
        <div>
          <Label className="text-xs">Morte (capitale)</Label>
          <Num value={p.morte} min={G.morte.min} max={G.morte.max} step={G.morte.step} suffix="€"
            onChange={(v) => set("morte", v)} testid={`fs-morte-${idx}`} />
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {["ricovero", "immobilizzazione", "spese_mediche"].map((key) => {
          const active = p[`${key}_attivo`];
          const isDiaria = G[key].tipo === "diaria";
          const field = isDiaria ? `${key}_diaria` : `${key}_massimale`;
          return (
            <div key={key} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-[#F0F4F1] px-3 py-2">
              <div className="flex items-center gap-2">
                <Switch checked={!!active} onCheckedChange={(v) => set(`${key}_attivo`, v)} data-testid={`fs-${key}-${idx}`} />
                <span className="text-xs font-medium text-forest">{G[key].label}</span>
              </div>
              {active && (
                <Num value={p[field]} min={G[key].min} max={G[key].max} step={G[key].step}
                  suffix={isDiaria ? "€/giorno" : "€"} onChange={(v) => set(field, v)} testid={`fs-${key}-val-${idx}`} />
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export function FormulaServiceForm({ cfg, value, onChange, calc, showFlex, readOnlyComposition, children }) {
  if (!cfg) return null;
  const v = value;
  const setP = (idx, np) => {
    const persone = [...v.persone];
    persone[idx] = np;
    onChange({ ...v, persone });
  };

  const setComposition = (type) => {
    const c = cfg.composizioni[type];
    if (!c) return;
    const persone = [];
    for (let i = 0; i < c.adulti; i++) persone.push({ ...cfg.default_persona, ruolo: "adulto" });
    const nFigli = c.figli_min || 0;
    for (let i = 0; i < nFigli; i++) persone.push({ ...cfg.default_persona, ruolo: "figlio" });
    onChange({ ...v, composition_type: type, persone });
  };

  const addFiglio = () => {
    if (v.persone.length >= cfg.max_persone) return;
    onChange({ ...v, persone: [...v.persone, { ...cfg.default_persona, ruolo: "figlio" }] });
  };
  const removeLast = () => {
    if (v.persone.length <= 1) return;
    onChange({ ...v, persone: v.persone.slice(0, -1) });
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      {/* Colonna configurazione */}
      <div className="space-y-5">
        {!readOnlyComposition && (
          <Card className="rounded-xl border-border/60 p-4">
            <Label className="mb-2 flex items-center gap-2 text-sm font-semibold text-forest"><Users className="h-4 w-4" /> Composizione familiare</Label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {Object.entries(cfg.composizioni).map(([k, c]) => (
                <button key={k} onClick={() => setComposition(k)} data-testid={`fs-comp-${k}`}
                  className={`rounded-lg border px-3 py-2 text-xs font-medium transition ${v.composition_type === k ? "border-terracotta bg-terracotta/10 text-terracotta" : "border-border/60 text-forest hover:border-terracotta/40"}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </Card>
        )}

        <div className="space-y-3">
          {v.persone.map((p, i) => <PersonPanel key={i} cfg={cfg} p={p} idx={i} onChange={setP} />)}
          <div className="flex gap-2">
            <button onClick={addFiglio} disabled={v.persone.length >= cfg.max_persone} data-testid="fs-add-figlio"
              className="rounded-full border border-border/60 px-3 py-1.5 text-xs text-forest hover:border-terracotta/40 disabled:opacity-40">+ Aggiungi figlio</button>
            <button onClick={removeLast} disabled={v.persone.length <= 1} data-testid="fs-remove-person"
              className="rounded-full border border-border/60 px-3 py-1.5 text-xs text-muted-foreground hover:border-red-300 disabled:opacity-40">− Rimuovi ultimo</button>
          </div>
        </div>

        <Card className="rounded-xl border-border/60 p-4">
          <Label className="mb-2 flex items-center gap-2 text-sm font-semibold text-forest"><Shield className="h-4 w-4" /> Ambito di rischio</Label>
          <RadioGroup value={v.ambito} onValueChange={(val) => onChange({ ...v, ambito: val })} className="space-y-2">
            {Object.entries(cfg.ambito).map(([k, a]) => (
              <label key={k} className="flex items-center gap-2.5 text-sm text-forest">
                <RadioGroupItem value={k} data-testid={`fs-ambito-${k}`} /> {a.label}
              </label>
            ))}
          </RadioGroup>
        </Card>

        <Card className="rounded-xl border-border/60 p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Tutela Legale</Label>
              <Select value={v.tutela_legale} onValueChange={(val) => onChange({ ...v, tutela_legale: val })}>
                <SelectTrigger className="mt-1 h-9" data-testid="fs-tutela-legale"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(cfg.tutela_legale).map(([k, o]) => <SelectItem key={k} value={k}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Frazionamento</Label>
              <Select value={v.frazionamento} onValueChange={(val) => onChange({ ...v, frazionamento: val })}>
                <SelectTrigger className="mt-1 h-9" data-testid="fs-frazionamento"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cfg.frazionamento.map((k) => <SelectItem key={k} value={k}>{FRAZ_LABEL[k] || k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {showFlex && (
          <Card className="rounded-xl border-terracotta/30 bg-terracotta/5 p-4">
            <Label className="mb-1 flex items-center gap-2 text-sm font-semibold text-terracotta"><Percent className="h-4 w-4" /> Flessibilità commerciale (solo intermediario)</Label>
            <p className="mb-3 text-xs text-muted-foreground">Riservato: non verrà mai mostrato al cliente. Da −20% a +100%.</p>
            <div className="flex items-center gap-4">
              <Slider min={cfg.flessibilita.min_pct} max={cfg.flessibilita.max_pct} step={cfg.flessibilita.step}
                value={[v.flessibilita_pct]} onValueChange={([val]) => onChange({ ...v, flessibilita_pct: val })}
                data-testid="fs-flex-slider" className="flex-1" />
              <span className="w-14 text-right font-mono text-sm font-semibold text-terracotta" data-testid="fs-flex-value">{v.flessibilita_pct > 0 ? "+" : ""}{v.flessibilita_pct}%</span>
            </div>
          </Card>
        )}
      </div>

      {/* Colonna ticket premio */}
      <div className="space-y-4 lg:sticky lg:top-20 lg:self-start">
        <Card className="overflow-hidden rounded-xl border-terracotta/30">
          <div className="bg-forest px-5 py-4 text-white">
            <p className="text-xs uppercase tracking-wide text-white/70">Premio lordo annuo</p>
            <p className="font-heading text-4xl font-bold" data-testid="fs-premio-annuo">{calc ? eur(calc.premio_lordo_annuo) : "—"}</p>
            {calc && calc.n_periodi > 1 && (
              <p className="mt-1 text-sm text-white/80" data-testid="fs-rata">{eur(calc.rata_periodo)} × {calc.n_periodi} rate</p>
            )}
          </div>
          {calc && (
            <div className="space-y-1.5 px-5 py-4 text-sm">
              {calc.persone.map((p, i) => (
                <div key={i} className="flex justify-between text-muted-foreground">
                  <span>{p.ruolo === "figlio" ? "Figlio" : "Adulto"} {i + 1}</span><span>{eur(p.premio_imponibile)}</span>
                </div>
              ))}
              <div className="flex justify-between border-t border-border/50 pt-1.5 text-forest"><span>Totale garanzie (imposte incl.)</span><span>{eur(calc.totale_garanzie)}</span></div>
              {calc.totale_tutela_legale > 0 && <div className="flex justify-between text-muted-foreground"><span>Tutela Legale</span><span>{eur(calc.totale_tutela_legale)}</span></div>}
              <div className="flex justify-between text-muted-foreground"><span>Assistenza</span><span>{eur(calc.totale_assistenza)}</span></div>
              {calc.minimo_applicato && <p className="pt-1 text-[11px] italic text-amber-600">È stato applicato il premio minimo di polizza.</p>}
            </div>
          )}
        </Card>
        {children}
        {cfg.trasparenza && (
          <Card className="rounded-xl border-border/60 bg-[#F0F4F1] p-4">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-forest"><Info className="h-3.5 w-3.5" /> Trasparenza e condizioni</p>
            <ul className="space-y-1.5 text-[11px] leading-relaxed text-muted-foreground">
              {cfg.trasparenza.map((t, i) => <li key={i}>• {t}</li>)}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}
